// Shared field-evidence contract: incidents, evidence-attachment metadata, and
// route/DAR report summaries (Task 60/71 foundation). One vocabulary for mobile/server/
// web BEFORE DB tables, queues, storage, PDFs, or supervisor review screens exist.
// Zero dependencies (belongs in @secops/shared).
//
// Redaction posture: free-text, label, attachment-name, and storage-key fields are
// validated to REJECT secret/token-shaped values (chk_ tokens, sha256 hashes, Supabase
// secret/service-role words, JWT/bearer, Expo push tokens) so a guard or importer can't
// leak a secret into an incident note, attachment name, or report label. The dedicated
// `sha256` field is the one place a 64-hex value is allowed. Raw file bytes and public
// URLs are intentionally NOT contract fields.

export const INCIDENT_CATEGORIES = ["safety", "security", "maintenance", "client_note", "other"] as const;
export type IncidentCategory = (typeof INCIDENT_CATEGORIES)[number];

export const INCIDENT_SEVERITIES = ["info", "low", "medium", "high", "critical"] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

export const INCIDENT_STATUSES = ["draft", "submitted", "under_review", "locked"] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

export const EVIDENCE_KINDS = ["photo", "video", "audio", "document", "signature"] as const;
export type EvidenceKind = (typeof EVIDENCE_KINDS)[number];

export const EVIDENCE_SOURCES = ["camera", "upload", "import", "unknown"] as const;
export type EvidenceSource = (typeof EVIDENCE_SOURCES)[number];

export const FIELD_EVIDENCE_STORAGE_BUCKET = "field-evidence";

/** Attachment METADATA only — never raw bytes, never a public URL. */
export interface EvidenceAttachment {
  readonly kind: EvidenceKind;
  readonly displayName: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly source: EvidenceSource;
  readonly capturedAt: string | null;
  /** Optional content-hash placeholder (sha256 hex). The one field allowed to be 64-hex. */
  readonly sha256: string | null;
  /** Optional storage-key placeholder (e.g. a bucket path). Never a public URL or secret. */
  readonly storageKey: string | null;
}

export interface FieldIncident {
  readonly clientEventId: string;
  readonly category: IncidentCategory;
  readonly severity: IncidentSeverity;
  readonly status: IncidentStatus;
  readonly title: string;
  readonly notes: string;
  readonly occurredAt: string;
  readonly reportedByLabel: string;
  readonly siteLabel: string | null;
  readonly routeLabel: string | null;
  readonly checkpointLabel: string | null;
  readonly attachments: readonly EvidenceAttachment[];
}

export interface RouteReportSummary {
  readonly generatedAt: string;
  readonly routeLabel: string;
  readonly siteLabel: string;
  readonly officerLabel: string;
  readonly shiftStart: string;
  readonly shiftEnd: string;
  readonly expectedCheckpointCount: number;
  readonly capturedCount: number;
  readonly validatedCount: number;
  readonly missedCount: number;
  readonly flaggedCount: number;
  readonly rejectedCount: number;
}

// Loose input shapes (untrusted JSON from a client). normalize* validate + return strict.
export interface EvidenceAttachmentInput {
  kind: string;
  displayName: string;
  contentType: string;
  sizeBytes: number;
  source?: string | null;
  capturedAt?: string | null;
  sha256?: string | null;
  storageKey?: string | null;
}

export const FIELD_INCIDENT_TITLE_MAX = 200;

export interface FieldIncidentInput {
  clientEventId: string;
  category: string;
  severity: string;
  status: string;
  title: string;
  notes: string;
  occurredAt: string;
  reportedByLabel: string;
  siteLabel?: string | null;
  routeLabel?: string | null;
  checkpointLabel?: string | null;
  attachments?: readonly EvidenceAttachmentInput[];
}

export interface RouteReportSummaryInput {
  generatedAt: string;
  routeLabel: string;
  siteLabel: string;
  officerLabel: string;
  shiftStart: string;
  shiftEnd: string;
  expectedCheckpointCount: number;
  capturedCount: number;
  validatedCount: number;
  missedCount: number;
  flaggedCount: number;
  rejectedCount: number;
}

export interface EvidenceStorageKeyInput {
  readonly accountId: string;
  readonly incidentId: string;
  readonly filename: string;
}

export class FieldEvidenceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FieldEvidenceError";
  }
}

// --- secret-shape detection ---------------------------------------------------
// Aligned with alert-push-payload's PRIVATE_VALUE_PATTERN, extended to the full set a
// field-evidence note / label / name / storage key must never carry. Patterns carry no
// `g` flag so `.test()` is stateless; the `g` flag is added only for scrub replacement.
const SECRET_SHAPES: ReadonlyArray<readonly [string, RegExp]> = [
  ["checkpoint_token", /\bchk_[A-Za-z0-9]{12,}\b/],
  ["token_hash", /\b[a-f0-9]{64}\b/i],
  ["supabase_secret", /\bsb_secret_[A-Za-z0-9]/i],
  ["service_role", /service_role/i],
  ["jwt", /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/],
  ["bearer", /\bbearer\s+[A-Za-z0-9._-]{8,}/i],
  ["expo_push", /\bExpo(?:nent)?PushToken\[/i],
];

/** Returns the name of the first secret/token shape found in `value`, or null. */
export function findSecretShape(value: string): string | null {
  if (typeof value !== "string") {
    return null;
  }
  for (const [name, pattern] of SECRET_SHAPES) {
    if (pattern.test(value)) {
      return name;
    }
  }
  return null;
}

export function containsSecretShape(value: string): boolean {
  return findSecretShape(value) !== null;
}

/** Replace any secret/token-shaped substrings with `[redacted]` (defense-in-depth display path). */
export function scrubSecretShapes(value: string): string {
  if (typeof value !== "string") {
    return value;
  }
  let out = value;
  for (const [, pattern] of SECRET_SHAPES) {
    const flags = pattern.flags.includes("g") ? pattern.flags : `${pattern.flags}g`;
    out = out.replace(new RegExp(pattern.source, flags), "[redacted]");
  }
  return out;
}

/** Throw if `value` carries a secret/token shape (fail-closed write-path validation). */
export function assertNoSecretShape(value: string, fieldLabel: string): void {
  const shape = findSecretShape(value);
  if (shape) {
    throw new FieldEvidenceError(
      `${fieldLabel} contains a ${shape}-shaped value; secrets and tokens must not appear in field evidence.`,
    );
  }
}

// --- normalizers --------------------------------------------------------------
const MIME_PATTERN = /^[a-z0-9][\w.+-]*\/[a-z0-9][\w.+-]*$/i;

export function normalizeEvidenceAttachment(input: EvidenceAttachmentInput): EvidenceAttachment {
  const kind = requireEnum(input.kind, EVIDENCE_KINDS, "attachment kind");
  const displayName = cleanText(input.displayName, "attachment display name");
  const contentType = requireString(input.contentType, "attachment content type");
  if (!MIME_PATTERN.test(contentType)) {
    throw new FieldEvidenceError("attachment content type must be a MIME type like image/jpeg.");
  }
  if (typeof input.sizeBytes !== "number" || !Number.isInteger(input.sizeBytes) || input.sizeBytes < 0) {
    throw new FieldEvidenceError("attachment size must be a non-negative integer byte count.");
  }
  const source = input.source == null || input.source === "" ? "unknown" : requireEnum(input.source, EVIDENCE_SOURCES, "attachment source");
  return {
    kind,
    displayName,
    contentType,
    sizeBytes: input.sizeBytes,
    source,
    capturedAt: optionalText(input.capturedAt),
    sha256: normalizeSha256(input.sha256),
    storageKey: optionalCleanText(input.storageKey, "attachment storage key"),
  };
}

export function normalizeFieldIncident(input: FieldIncidentInput): FieldIncident {
  return {
    // clientEventId is an untrusted client-supplied idempotency key that gets stored,
    // logged, indexed, and echoed in sync/reporting flows — guard it like the text fields
    // so a bad client/importer can't smuggle a token through it (Task 71 review, Codex P2).
    clientEventId: cleanText(input.clientEventId, "incident clientEventId"),
    category: requireEnum(input.category, INCIDENT_CATEGORIES, "incident category"),
    severity: requireEnum(input.severity, INCIDENT_SEVERITIES, "incident severity"),
    status: requireEnum(input.status, INCIDENT_STATUSES, "incident status"),
    title: capIncidentTitle(cleanText(input.title, "incident title")),
    notes: cleanText(input.notes, "incident notes"),
    occurredAt: requireString(input.occurredAt, "incident occurredAt"),
    reportedByLabel: cleanText(input.reportedByLabel, "incident reportedBy label"),
    siteLabel: optionalCleanText(input.siteLabel, "incident site label"),
    routeLabel: optionalCleanText(input.routeLabel, "incident route label"),
    checkpointLabel: optionalCleanText(input.checkpointLabel, "incident checkpoint label"),
    attachments: (input.attachments ?? []).map((attachment) => normalizeEvidenceAttachment(attachment)),
  };
}

export function normalizeRouteReportSummary(input: RouteReportSummaryInput): RouteReportSummary {
  return {
    generatedAt: requireString(input.generatedAt, "report generatedAt"),
    routeLabel: cleanText(input.routeLabel, "report route label"),
    siteLabel: cleanText(input.siteLabel, "report site label"),
    officerLabel: cleanText(input.officerLabel, "report officer label"),
    shiftStart: requireString(input.shiftStart, "report shiftStart"),
    shiftEnd: requireString(input.shiftEnd, "report shiftEnd"),
    expectedCheckpointCount: requireCount(input.expectedCheckpointCount, "expected checkpoint count"),
    capturedCount: requireCount(input.capturedCount, "captured count"),
    validatedCount: requireCount(input.validatedCount, "validated count"),
    missedCount: requireCount(input.missedCount, "missed count"),
    flaggedCount: requireCount(input.flaggedCount, "flagged count"),
    rejectedCount: requireCount(input.rejectedCount, "rejected count"),
  };
}

/** Derived coverage for a report: outstanding checkpoints + validated coverage percent. */
export function reportCompleteness(summary: RouteReportSummary): { coveragePercent: number; outstanding: number } {
  const expected = summary.expectedCheckpointCount;
  return {
    coveragePercent: expected > 0 ? Math.round((summary.validatedCount / expected) * 100) : 0,
    outstanding: Math.max(0, expected - summary.capturedCount),
  };
}

export function buildEvidenceStorageKey(input: EvidenceStorageKeyInput): string {
  const accountId = requireUuid(input.accountId, "account id");
  const incidentId = requireUuid(input.incidentId, "incident id");
  const filename = normalizeStorageFilename(input.filename);
  const storageKey = `accounts/${accountId}/incidents/${incidentId}/${filename}`;
  assertNoSecretShape(storageKey, "evidence storage key");
  return storageKey;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function requireString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new FieldEvidenceError(`${label} is required.`);
  }
  return value.trim();
}

function cleanText(value: unknown, label: string): string {
  const trimmed = requireString(value, label);
  assertNoSecretShape(trimmed, label);
  return trimmed;
}

function capIncidentTitle(title: string): string {
  if (title.length > FIELD_INCIDENT_TITLE_MAX) {
    throw new FieldEvidenceError(
      `incident title must be ${FIELD_INCIDENT_TITLE_MAX} characters or fewer.`,
    );
  }
  return title;
}

function optionalText(value: string | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function optionalCleanText(value: string | null | undefined, label: string): string | null {
  const trimmed = optionalText(value);
  if (trimmed === null) {
    return null;
  }
  assertNoSecretShape(trimmed, label);
  return trimmed;
}

function requireEnum<T extends string>(value: string, allowed: readonly T[], label: string): T {
  if (!(allowed as readonly string[]).includes(value)) {
    throw new FieldEvidenceError(`${label} must be one of: ${allowed.join(", ")}.`);
  }
  return value as T;
}

function requireCount(value: number, label: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    throw new FieldEvidenceError(`${label} must be a non-negative integer count.`);
  }
  return value;
}

function requireUuid(value: unknown, label: string): string {
  const trimmed = requireString(value, label).toLowerCase();
  if (!UUID_PATTERN.test(trimmed)) {
    throw new FieldEvidenceError(`${label} must be a UUID.`);
  }
  return trimmed;
}

function normalizeStorageFilename(value: unknown): string {
  const raw = cleanText(value, "evidence filename");
  if (
    raw.includes("/")
    || raw.includes("\\")
    || raw.startsWith(".")
    || raw.includes("..")
    || /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)
  ) {
    throw new FieldEvidenceError("evidence filename must be a plain filename, not a path or URL.");
  }

  const normalized = raw.toLowerCase().replace(/\s+/g, "-");
  if (!/^[a-z0-9][a-z0-9._-]*[a-z0-9]$/.test(normalized)) {
    throw new FieldEvidenceError(
      "evidence filename may contain only letters, numbers, spaces, dots, underscores, and dashes.",
    );
  }
  return normalized;
}

function normalizeSha256(value: string | null | undefined): string | null {
  const trimmed = optionalText(value);
  if (trimmed === null) {
    return null;
  }
  const lower = trimmed.toLowerCase();
  if (!/^[a-f0-9]{64}$/.test(lower)) {
    throw new FieldEvidenceError("attachment sha256 must be a 64-character hex digest when provided.");
  }
  return lower;
}
